<template>
  <div class="kakao-share-section">
    <a-divider orientation="left">카카오 공유하기 버튼 설정</a-divider>

    <a-alert
      message="카카오 공유 설정 가이드"
      type="info"
      show-icon
      style="margin-bottom: 24px"
    >
      <template #description>
        <ul style="margin: 8px 0 0; padding-left: 20px;">
          <li>카카오톡 공유 시 표시될 정보를 설정합니다.</li>
          <li>이미지는 HTTPS 프로토콜을 사용하고, 800x400px 이상 권장합니다.</li>
          <li>모든 필드는 선택사항입니다. 입력하지 않으면 기본값이 사용됩니다.</li>
        </ul>
      </template>
    </a-alert>

    <a-row :gutter="24">
      <a-col :span="12">
        <a-form-item
          label="공유 제목"
          name="kakaoShareBtnTitle"
        >
          <a-input
            v-model:value="form.kakaoShareBtn.title"
            placeholder="예: 올리브영 상품권 도전하기"
            :maxLength="50"
            show-count
          />
          <div class="field-helper">카카오톡 공유 시 표시될 제목</div>
        </a-form-item>
      </a-col>

      <a-col :span="12">
        <a-form-item
          label="버튼 텍스트"
          name="kakaoShareBtnButtonText"
        >
          <a-input
            v-model:value="form.kakaoShareBtn.buttonText"
            placeholder="예: 이벤트 참여하기"
            :maxLength="30"
            show-count
          />
          <div class="field-helper">공유 메시지의 버튼 텍스트</div>
        </a-form-item>
      </a-col>
    </a-row>

    <a-row :gutter="24">
      <a-col :span="24">
        <a-form-item
          label="공유 설명문"
          name="kakaoShareBtnDescription"
        >
          <a-textarea
            v-model:value="form.kakaoShareBtn.description"
            placeholder="예: 친구와 함께 올리브영 상품권에 도전해보세요!"
            :maxLength="100"
            :rows="3"
            show-count
          />
          <div class="field-helper">공유 메시지 설명문</div>
        </a-form-item>
      </a-col>
    </a-row>

    <a-row :gutter="24">
      <a-col :span="24">
        <a-form-item
          label="썸네일 이미지"
          name="kakaoShareBtnImageUrl"
        >
          <!-- 이미지가 이미 있을 경우 미리보기 및 삭제 버튼 -->
          <div v-if="form.kakaoShareBtn.imageUrl && isValidImageUrl" class="image-preview-container">
            <img
              :src="form.kakaoShareBtn.imageUrl"
              alt="썸네일 미리보기"
              @error="handleImageError"
              class="preview-image-display"
            />
            <div style="margin-top: 8px; display: flex; gap: 8px;">
              <a-button
                size="small"
                type="text"
                danger
                @click="removeImage"
              >
                <i class="fa fa-trash"></i> 이미지 삭제
              </a-button>
              <a-button
                size="small"
                @click="showResourceModal = true"
              >
                <template #icon><i class="fa fa-folder-open"></i></template>
                자료실에서 선택
              </a-button>
            </div>
          </div>

          <!-- 입력 방식 선택 -->
          <div v-if="!form.kakaoShareBtn.imageUrl || !isValidImageUrl">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
              <a-radio-group v-model:value="uploadMode" size="small">
                <a-radio-button value="url">URL 입력</a-radio-button>
                <a-radio-button value="upload">파일 업로드</a-radio-button>
              </a-radio-group>
              <a-button
                size="small"
                @click="showResourceModal = true"
              >
                <template #icon><i class="fa fa-folder-open"></i></template>
                자료실에서 선택
              </a-button>
            </div>

            <!-- URL 입력 모드 -->
            <div v-if="uploadMode === 'url'">
              <a-input
                v-model:value="form.kakaoShareBtn.imageUrl"
                placeholder="https://example.com/share-image.png"
                @blur="validateImageUrl"
              />
            </div>

            <!-- 파일 업로드 모드 -->
            <div v-else>
              <!-- 이미지 크기 선택 -->
              <div style="margin-bottom: 12px;">
                <a-radio-group v-model:value="isWideImage" size="small">
                  <a-radio :value="false">일반 이미지 (800x400px 권장)</a-radio>
                  <a-radio :value="true">와이드 이미지 (800x200px 권장)</a-radio>
                </a-radio-group>
              </div>

              <div class="upload-area">
                <a-upload
                  :before-upload="beforeUpload"
                  :custom-request="customUpload"
                  :show-upload-list="false"
                  accept="image/*"
                >
                  <div class="upload-button">
                    <div v-if="!uploading">
                      <i class="fa fa-cloud-upload"></i>
                      <div class="upload-text">클릭하여 이미지 선택</div>
                      <div class="upload-hint">
                        {{ isWideImage ? '800x200px 권장 (와이드)' : '800x400px 권장 (일반)' }}
                      </div>
                    </div>
                    <div v-else>
                      <a-spin />
                      <div class="upload-text">카카오톡에 업로드 중...</div>
                    </div>
                  </div>
                </a-upload>
              </div>
            </div>
          </div>

          <div class="field-helper">공유 시 표시될 썸네일 이미지 (HTTPS 권장, 800x400px 이상)</div>
        </a-form-item>
      </a-col>
    </a-row>

    <a-row :gutter="24">
      <a-col :span="24">
        <a-form-item
          label="버튼 링크 URL"
          name="kakaoShareBtnBtnLink"
        >
          <a-input
            v-model:value="form.kakaoShareBtn.btnLink"
            placeholder="https://event.example.com/landing/123"
          />
          <div class="field-helper">버튼 클릭 시 이동할 URL (실제 이벤트 랜딩 페이지)</div>
        </a-form-item>
      </a-col>
    </a-row>

    <a-divider />

    <!-- 미리보기 섹션 -->
    <div class="preview-section">
      <h4 style="margin-bottom: 16px;">
        <i class="fa fa-eye"></i> 카카오 공유 미리보기
      </h4>

      <div class="kakao-preview-container">
        <div class="kakao-message-preview">
          <div class="preview-image" v-if="form.kakaoShareBtn.imageUrl">
            <img
              :src="form.kakaoShareBtn.imageUrl"
              alt="미리보기"
              @error="handlePreviewError"
            />
          </div>
          <div v-else class="preview-image-placeholder">
            <i class="fa fa-image"></i>
            <span>이미지 미리보기</span>
          </div>

          <div class="preview-content">
            <div class="preview-title">
              {{ form.kakaoShareBtn.title || '제목을 입력해주세요' }}
            </div>
            <div class="preview-description">
              {{ form.kakaoShareBtn.description || '설명문을 입력해주세요' }}
            </div>
          </div>

          <div class="preview-button" v-if="form.kakaoShareBtn.buttonText">
            {{ form.kakaoShareBtn.buttonText }}
          </div>
        </div>
      </div>
    </div>

    <!-- 자료실 모달 -->
    <EventResourceModal
      :open="showResourceModal"
      :api="eventApi"
      @select="handleFileSelect"
      @cancel="showResourceModal = false"
    />
  </div>
</template>

<script>
import { ref, watch, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import EventResourceModal from '../EventResourceModal.vue';
import eventsApis from '@/views/Events/api';

export default {
  name: 'KakaoShareSection',
  components: {
    EventResourceModal
  },
  props: {
    form: {
      type: Object,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { proxy: app } = getCurrentInstance();
    const isValidImageUrl = ref(true);
    const uploadMode = ref('url');
    const uploading = ref(false);
    const isWideImage = ref(false); // 와이드 이미지 여부
    const showResourceModal = ref(false);

    // API 인스턴스 생성
    const instance = getCurrentInstance();
    const apiInstance = instance?.proxy?.$apiInstance;
    const store = instance?.proxy?.$store;
    const eventApi = eventsApis(apiInstance, store);

    // 이미지 URL 유효성 검증
    const validateImageUrl = () => {
      const url = props.form.kakaoShareBtn.imageUrl;
      if (!url) {
        isValidImageUrl.value = true;
        return;
      }

      try {
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'https:' && urlObj.protocol !== 'http:') {
          message.warning('이미지 URL은 HTTP 또는 HTTPS 프로토콜을 사용해야 합니다.');
          isValidImageUrl.value = false;
        } else {
          isValidImageUrl.value = true;
        }
      } catch (e) {
        message.error('올바른 URL 형식이 아닙니다.');
        isValidImageUrl.value = false;
      }
    };

    // 이미지 로드 에러 처리
    const handleImageError = () => {
      message.error('이미지를 불러올 수 없습니다. URL을 확인해주세요.');
      isValidImageUrl.value = false;
    };

    const handlePreviewError = (event) => {
      event.target.style.display = 'none';
    };

    // 이미지 삭제
    const removeImage = () => {
      props.form.kakaoShareBtn.imageUrl = '';
      isValidImageUrl.value = true;
    };

    // 파일 업로드 전 검증
    const beforeUpload = (file) => {
      const isImage = file.type.indexOf('image/') === 0;
      if (!isImage) {
        message.error('이미지 파일만 업로드 가능합니다!');
        return false;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('이미지는 5MB 이하여야 합니다!');
        return false;
      }

      return true;
    };

    // 커스텀 업로드 처리 - 카카오톡 친구톡 이미지 업로드 API 사용
    const customUpload = async ({ file }) => {
      try {
        uploading.value = true;

        // FormData 생성
        const formData = new FormData();
        formData.append('file', file);

        // 카카오톡 친구톡 이미지 업로드 API 호출 (와이드 옵션 적용)
        const response = await app.$apiInstance.post(
          `/integrations/friendtalk/image/upload-file?isWide=${isWideImage.value}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            timeout: 120000,
            onUploadProgress: function(progressEvent) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log('업로드 진행률:', percentCompleted + '%');
            }
          }
        );

        // 업로드 성공 처리
        if (response.data && response.data.success && response.data.uploadedUrl) {
          const uploadedFileUrl = response.data.uploadedUrl;
          console.log('카카오톡에 업로드된 이미지 URL:', uploadedFileUrl);

          // 폼 데이터 업데이트
          props.form.kakaoShareBtn.imageUrl = uploadedFileUrl;
          isValidImageUrl.value = true;
          message.success('카카오톡 이미지가 성공적으로 업로드되었습니다.');
        } else {
          throw new Error('업로드 응답이 올바르지 않습니다.');
        }
      } catch (error) {
        console.error('카카오톡 이미지 업로드 실패:', error);
        if (error.response?.data?.message) {
          message.error(`업로드 실패: ${error.response.data.message}`);
        } else {
          message.error('카카오톡 이미지 업로드에 실패했습니다. 다시 시도해주세요.');
        }
      } finally {
        uploading.value = false;
      }
    };

    // 자료실에서 파일 선택 핸들러
    const handleFileSelect = (file) => {
      props.form.kakaoShareBtn.imageUrl = file.url;
      isValidImageUrl.value = true;
      showResourceModal.value = false;
      message.success('이미지가 선택되었습니다.');
    };

    // form.kakaoShareBtn이 없으면 초기화
    watch(() => props.form, (newForm) => {
      if (!newForm.kakaoShareBtn) {
        newForm.kakaoShareBtn = {
          title: '',
          description: '',
          imageUrl: '',
          buttonText: '',
          btnLink: ''
        };
      }
    }, { immediate: true, deep: true });

    return {
      isValidImageUrl,
      uploadMode,
      uploading,
      isWideImage,
      showResourceModal,
      eventApi,
      validateImageUrl,
      handleImageError,
      handlePreviewError,
      removeImage,
      beforeUpload,
      customUpload,
      handleFileSelect
    };
  }
};
</script>

<style scoped>
.kakao-share-section {
  padding: 20px 0;
}

.field-helper {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.image-preview-container {
  margin-bottom: 16px;
}

.preview-image-display {
  max-width: 100%;
  max-height: 200px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  display: block;
}

.upload-area {
  margin-top: 8px;
}

.upload-button {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;
}

.upload-button:hover {
  border-color: #ffc107;
  background-color: #fffef5;
}

.upload-button i {
  font-size: 48px;
  color: #bbb;
  margin-bottom: 12px;
}

.upload-text {
  font-size: 14px;
  color: #666;
  margin-top: 8px;
}

.upload-hint {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.preview-section {
  background: #f5f5f5;
  padding: 24px;
  border-radius: 8px;
  margin-top: 24px;
}

.kakao-preview-container {
  display: flex;
  justify-content: center;
  padding: 20px;
  background: white;
  border-radius: 8px;
}

.kakao-message-preview {
  width: 360px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-image, .preview-image-placeholder {
  width: 100%;
  height: 180px;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-image-placeholder {
  flex-direction: column;
  gap: 8px;
  color: #999;
}

.preview-image-placeholder i {
  font-size: 48px;
}

.preview-content {
  padding: 16px;
}

.preview-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.preview-description {
  font-size: 14px;
  color: #666;
  line-height: 1.5;
}

.preview-button {
  margin: 16px;
  padding: 12px;
  background: #fee500;
  color: #3c1e1e;
  text-align: center;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.preview-button:hover {
  background: #fdd835;
}
</style>